import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { createDraftsSlice, type DraftsSlice } from './drafts-slice.js';
import { createPrefsSlice, type PrefsSlice } from './prefs-slice.js';
import { createUiSlice, type UiSlice } from './ui-slice.js';

/**
 * The client-state tier.
 *
 * One store, composed from slices, subscribed **only by selector**:
 *
 *   const collapsed = useAppStore((state) => state.ui.sidebarCollapsed);
 *
 * Never `useAppStore()` whole. The selector is the entire reason this is a
 * store rather than a React context: a context value change re-renders every
 * consumer, so collapsing the sidebar would re-render every mounted table
 * unless each one were hand-memoised. That property is why Task 21's "no
 * global state library" is deviated from, recorded in the design spec.
 *
 * It holds no server data. Anything the server owns belongs in TanStack
 * Query, and anything a pasted link should reproduce belongs in the URL.
 */
export type AppState = UiSlice & PrefsSlice & DraftsSlice;

/**
 * The slices written to storage.
 *
 * Exported so the whitelist is assertable rather than a comment. Two
 * deliberate exclusions:
 *
 * - **The session.** The access token never enters this store; it stays in
 *   `ApiClient`. A token in web storage turns any XSS into a takeover that
 *   outlives the tab.
 * - **Drafts.** A half-typed guest record in `localStorage` is personal data
 *   at rest that nobody consented to and no retention policy covers.
 */
export const PERSISTED_SLICES = ['ui', 'prefs'] as const;

const STORAGE_KEY = 'ibe-admin-preferences';

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (...args) => ({
        ...createUiSlice(...args),
        ...createPrefsSlice(...args),
        ...createDraftsSlice(...args),
      }),
      {
        name: STORAGE_KEY,
        // `storage` is deliberately omitted: persist defaults to
        // `localStorage` with JSON serialisation, which is exactly what is
        // wanted, and naming it explicitly only adds an API surface to get
        // wrong across a zustand upgrade.
        //
        // The whitelist, applied. `partialize` decides what is written;
        // anything not named here never reaches storage.
        partialize: (state) => ({ ui: state.ui, prefs: state.prefs }),
        version: 1,
        // Merge the persisted slice with the factory defaults so a newly-added
        // `ui.lastPropertyIds` field is not missing when a store created before
        // it was added is rehydrated.
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<AppState> | undefined;
          return {
            ...currentState,
            ...(persisted ?? {}),
            ui: { ...currentState.ui, ...(persisted?.ui ?? {}) },
            prefs: { ...currentState.prefs, ...(persisted?.prefs ?? {}) },
          };
        },
      },
    ),
    { name: 'ibe-admin', enabled: import.meta.env.DEV },
  ),
);
